export interface PanelPlacement {
  top: number;
  left: number;
  /** Width to actually render at — never wider than the viewport allows. */
  width: number;
  /** Max height to render at — never taller than the viewport allows. */
  height: number;
}

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(v, max));

/**
 * Fixed-position placement for a hover panel anchored next to an element.
 * Prefers the right of the anchor and flips left when it wouldn't fit, then
 * **clamps** the result inside the viewport margins — on a phone the panel can
 * be as wide as the screen, so "flip to the left" alone would push it off the
 * edge. The returned `width`/`height` are the requested size capped to what
 * fits between the margins; render at those, not at the requested size, or the
 * clamped position still overflows.
 *
 * Pure so it can be unit-tested without a DOM. (QA #10 / WMS-11, WMS-37.)
 */
export function panelPosition(
  rect: { top: number; right: number; left: number },
  viewport: { width: number; height: number },
  size: { width: number; height: number },
  gap = 8,
): PanelPlacement {
  const width = Math.min(size.width, Math.max(0, viewport.width - 2 * gap));
  const height = Math.min(size.height, Math.max(0, viewport.height - 2 * gap));
  const preferred =
    rect.right + gap + width <= viewport.width ? rect.right + gap : rect.left - width - gap;
  return {
    width,
    height,
    left: clamp(preferred, gap, Math.max(gap, viewport.width - width - gap)),
    top: clamp(rect.top, gap, Math.max(gap, viewport.height - height - gap)),
  };
}

/** `panelPosition` for a square preview image (the photo hover-zoom). */
export function previewPosition(
  rect: { top: number; right: number; left: number },
  viewport: { width: number; height: number },
  size: number,
  gap = 8,
): PanelPlacement {
  return panelPosition(rect, viewport, { width: size, height: size }, gap);
}
